import { useAsyncCallback } from '@wix/cli-ui-kit';
import { useDevCenterClient } from '@wix/dev-center-client';
import type { CloudProvider } from '@wix/backend-as-a-service-client';
import { useBackendAsAServiceClient } from '@wix/backend-as-a-service-client';
import { generateProjectSlug } from '../project-slug.js';

const DEFAULT_ENVIRONMENT_NAME = 'system_global';

export interface ProjectData {
  projectId: string;
  businessName: string;
  instanceId: string;
  appSecret: string;
  webhookPublicKey: string;
  environmentVariables: Record<string, string>;
  baseUrl: string;
}

function truncateName(name: string, maxLength: number) {
  if (name.length > maxLength) {
    return `${name.slice(0, maxLength - 3)}...`;
  }

  return name;
}

export const useCreateProject = (businessId: string) => {
  const {
    updateOAuthApp,
    getAppSecret,
    createCompanionApp,
    installApp,
    setNamespace,
  } = useDevCenterClient({
    siteId: businessId,
  });
  const { createAppProject, upsertAppEnvironmentVariables } =
    useBackendAsAServiceClient();

  return useAsyncCallback(
    async (
      _,
      {
        businessName,
        cloudProviders,
      }: { businessName: string; cloudProviders?: CloudProvider[] }
    ) => {
      const { id: projectId } = await createCompanionApp();

      const slug = generateProjectSlug(businessName);

      await setNamespace({
        appId: projectId,
        namespace: slug,
      });

      const appInstall = await installApp(businessId, projectId);

      const publicKeyAndSecret = await getAppSecret({ appId: projectId });

      const appProject = await createAppProject({
        id: projectId,
        displayName: truncateName(businessName, 50),
        slug,
        cloudProvider: cloudProviders?.[0],
        projectType: 'HEADLESS',
      });

      const loginRoute = `api/auth/callback`;
      const logoutRoute = `api/auth/logout-callback`;
      const projectBaseURL = new URL(appProject.baseUrl);

      const localBaseDomain = `http://localhost:4321`;
      const previewBaseDomain = `https://(.*)-${projectBaseURL.hostname}`;
      const previewBaseRedirect = `https://*-${projectBaseURL.hostname}`;
      const prodBaseDomain = projectBaseURL.toString().replace(/\/$/, '');

      await updateOAuthApp({
        id: projectId,
        allowedDomains: [localBaseDomain, previewBaseDomain, prodBaseDomain],
        allowedRedirectUris: [
          `${localBaseDomain}/${loginRoute}`,
          `${localBaseDomain}/${logoutRoute}`,
          `${previewBaseRedirect}/${loginRoute}`,
          `${previewBaseRedirect}/${logoutRoute}`,
          `${prodBaseDomain}/${loginRoute}`,
          `${prodBaseDomain}/${logoutRoute}`,
        ],
        redirectUrlWixPages: prodBaseDomain,
        origin: 'other',
      });

      const environmentVariables = await upsertAppEnvironmentVariables(
        appProject.id,
        DEFAULT_ENVIRONMENT_NAME,
        {
          WIX_CLIENT_ID: projectId,
          WIX_CLIENT_SECRET: publicKeyAndSecret.appSecret,
          WIX_CLIENT_PUBLIC_KEY: publicKeyAndSecret.webhookPublicKey,
          WIX_CLIENT_INSTANCE_ID: appInstall.appInstance.id,
        }
      );

      const projectData: ProjectData = {
        projectId,
        businessName,
        instanceId: appInstall.appInstance.id,
        appSecret: publicKeyAndSecret.appSecret,
        webhookPublicKey: publicKeyAndSecret.webhookPublicKey,
        environmentVariables,
        baseUrl: appProject.baseUrl,
      };

      return projectData;
    }
  );
};
